import { memo, useState } from "react";

import { useGetUserInfo } from "@synthesis/shared-services/auth/user/hooks";
import type { UserDto } from "@synthesis/shared-services/auth/user/types";
import { IconClose, IconMenuHorizontalDots } from "@synthesis/shared-icons";
import { Button, Dropdown, useSidebar, UserInformation, type StateProps, type UserInformationUser } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { useNotificationAction } from "../hooks/use-notification-action";
import {
  formatNotificationDate,
  createNotificationItemDropdownScheme,
  useNotificationsStore,
  type NotificationViewModel,
} from "../notifications-panel.lib";
import { useApplicationNotifications } from "../use-application-notifications";
import { NotificationBody } from "./notification-body";

type NotificationItemProps = {
  customNotice?: boolean;
  notification: NotificationViewModel;
  className?: string;
};

const toUserInformationUser = (user: UserDto | null | undefined): UserInformationUser | null => {
  if (!user) {
    return null;
  }

  return {
    email: user.email ?? undefined,
    enabled: user.enabled ?? undefined,
    firstName: user.firstName ?? undefined,
    groups: user.groups?.map((group) => ({ id: group.id ?? undefined, name: group.name ?? undefined })) ?? undefined,
    id: user.id ?? undefined,
    lastName: user.lastName ?? undefined,
    middleName: user.middleName ?? undefined,
    position: user.position ?? undefined,
  };
};

export const NotificationItem = memo(({ className, customNotice = false, notification }: NotificationItemProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { close } = useSidebar();
  const store = useNotificationsStore()!;
  const { deleteNotification, markNotificationAsRead } = useApplicationNotifications();
  const handleAction = useNotificationAction();

  const { adapter } = notification;
  const source = notification.notification;
  const actions = adapter.getActions();
  const title = adapter.getTitle();
  const date = formatNotificationDate(notification.createdAt);
  const { body, canShowButton = true } = adapter.getBody({ onClose: close });
  const authorId = typeof source.params.authorId === "string" ? source.params.authorId : undefined;
  const authorName = source.params.notificationTriggerUserName;
  const authorQuery = useGetUserInfo({ path: { userId: authorId ?? "" } }, { query: { enabled: false } });
  const author = toUserInformationUser(authorQuery.data);

  const handleMarkAsRead = () => {
    markNotificationAsRead(notification.id);
  };

  const handleDelete = () => {
    deleteNotification(notification.id);
  };

  const handleAuthorClick = () => {
    if (authorId) {
      void authorQuery.refetch();
    }
  };

  const dropdownState: StateProps = {
    delete: handleDelete,
    markAsRead: handleMarkAsRead,
  };

  const openModal = () => {
    store.openModal({
      actions,
      author: authorName,
      body,
      date,
      isNew: notification.isNew,
      isShowButton: canShowButton,
      notification: source,
      notificationId: notification.id,
      title,
    });
    close();
  };

  return (
    <article
      data-testid="eacd-notification-item"
      className={cn(
        "bg-neutrals-white group relative flex flex-col gap-3 rounded-[14px] p-4 shadow-[0_2px_3px_rgba(32,26,26,0.04),0_5px_9px_rgba(32,26,26,0.06)]",
        className,
      )}
    >
      {notification.isNew && !customNotice && (
        <span className="bg-system-error-50 absolute -left-3 top-6 size-2.5 rounded-full" aria-label="Новое уведомление" />
      )}

      <div className="flex items-center gap-2">
        <button type="button" className="subtitle-sm text-type-common flex min-w-0 flex-1 items-center gap-2 text-left" onClick={openModal}>
          <span className="text-neutrals-60 size-5 shrink-0">{adapter.getIcon?.()}</span>
          <span className="truncate">{title}</span>
        </button>

        <p className="subtitle-xs text-type-light shrink-0 px-1">{date}</p>

        <div className="border-neutrals-95 flex items-center gap-2 self-stretch border-l pl-2">
          {customNotice ? (
            <Button type="button" size="xs" theme="text" onClick={close}>
              <IconClose className="text-neutrals-60 size-5" />
            </Button>
          ) : (
            <Dropdown
              align="end"
              portal={false}
              scheme={createNotificationItemDropdownScheme(notification.isNew)}
              size="sm"
              state={dropdownState}
              onOpenChange={setIsDropdownOpen}
              trigger={
                <button
                  type="button"
                  className={cn(
                    "hover:bg-neutrals-95 flex size-6 items-center justify-center rounded-lg transition-colors",
                    isDropdownOpen && "bg-neutrals-95",
                  )}
                  aria-label="Действия с уведомлением"
                >
                  <IconMenuHorizontalDots className="text-neutrals-60 size-5" aria-hidden />
                </button>
              }
            />
          )}
        </div>
      </div>

      <div className="paragraph-base text-type-minor">
        <NotificationBody>{body}</NotificationBody>
      </div>

      <div className="flex items-center justify-between gap-4">
        {adapter.getAuthor && (
          <UserInformation
            deletedUser={!authorName}
            start
            trigger={<span onClick={handleAuthorClick}>{adapter.getAuthor(authorName)}</span>}
            user={author}
          />
        )}

        {canShowButton && actions.length > 0 && (
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            {actions.map((action) => (
              <Button
                type="button"
                theme="secondary"
                size="sm"
                className="w-fit"
                key={`${action.title}-${JSON.stringify(action.payload)}`}
                onClick={() => handleAction(action)}
              >
                {action.title}
              </Button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

NotificationItem.displayName = "NotificationItem";
